import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { HiShare, HiStar } from 'react-icons/hi';
import LoadingSkeleton from '../../components/ui/LoadingSkeleton/LoadingSkeleton';
import EmptyState from '../../components/ui/EmptyState/EmptyState';
import CommunityEventCard from '../../components/events/CommunityEventCard/CommunityEventCard';
import Button from '../../components/ui/Button/Button';
import { useCommunityDetails } from '../../hooks/useCommunityDetails';
import { useChannels } from '../../hooks/useChannels';
import { useGeneral } from '../../contexts';
import Services from '../../services/Services';
import { generateEventUrl, createSlug, extractChannelIdFromUrl, generateCommunityShareUrl } from '../../utils/urlSlug';
import { EventListItem } from '../../types';
import { ChannelUser, Announcement } from '../../types/service.types';
import Modal from '../../components/ui/Modal/Modal';
import { SoldTicket } from '../../types/ticket.types';
import GrayBanner from '../../components/ui/GrayBanner/GrayBanner';
import ChannelInitialIcon from '../../components/ui/ChannelInitialIcon/ChannelInitialIcon';
import UserProfileLink from '../../components/ui/UserDisplay/UserProfileLink';
import PrivateChannelError from '../../components/ui/PrivateChannelError/PrivateChannelError';
import ParticipantListModal from '../../components/events/ParticipantListModal/ParticipantListModal';
import OrganizersModal from '../../components/communities/OrganizersModal/OrganizersModal';
import { getPublicToken } from '../../utils/publicToken';
import SEO from '../../components/SEO';
import { generateOrganizationSchema } from '../../utils/seoSchemas';
import styles from './CommunityPage.module.css';

const CommunityPage: React.FC = () => {
  const { communityPath } = useParams<{ communityPath: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'upcoming' | 'past'>('upcoming');
  const [channelID, setChannelID] = useState<string | null>(null);
  const [slugLoading, setSlugLoading] = useState(true);
  const [hasCheckedForID, setHasCheckedForID] = useState(false);
  const [bannerLoading, setBannerLoading] = useState(true);
  const [logoLoading, setLogoLoading] = useState(true);
  const [admins, setAdmins] = useState<ChannelUser[]>([]);
  const [members, setMembers] = useState<ChannelUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [showParticipantModal, setShowParticipantModal] = useState(false);
  const [showOrganizersModal, setShowOrganizersModal] = useState(false);
  const [bannerError, setBannerError] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const [channelAnnouncements, setChannelAnnouncements] = useState<Announcement[]>([]);
  const [announcementsLoading, setAnnouncementsLoading] = useState(false);
  const [selectedAnnouncementDetail, setSelectedAnnouncementDetail] = useState<Announcement | null>(null);
  const [showAnnouncementDetailModal, setShowAnnouncementDetailModal] = useState(false);
  
  const { language, auth, toastSuccess, toastError } = useGeneral();
  const { token, user } = auth;
  const { getTranslation } = language;
  const userId = user?.id || '';

  const displayValue = (value: any, fallback: string = getTranslation('common.notAvailable')): string => {
    if (value === null || value === undefined || value === '') {
      return fallback;
    }
    return String(value);
  };

  // Color palette for avatar backgrounds (same as ParticipantListModal)
  const AVATAR_COLORS = [
    '#EF4444', '#F97316', '#F59E0B', '#EAB308', '#84CC16', '#22C55E',
    '#10B981', '#14B8A6', '#06B6D4', '#0EA5E9', '#3B82F6', '#6366F1',
    '#8B5CF6', '#A855F7', '#D946EF', '#EC4899', '#F43F5E', '#64748B',
  ];

  const getColorForName = (name: string): string => {
    if (!name || name.length === 0) {
      return AVATAR_COLORS[0];
    }
    const firstLetter = name.charAt(0).toUpperCase();
    const charCode = firstLetter.charCodeAt(0);
    const index = (charCode - 65) % AVATAR_COLORS.length;
    return AVATAR_COLORS[index];
  };

  // Helper function to get join date from ChannelUser
  const getJoinDate = (user: ChannelUser): number => {
    // Try various possible date fields (as number or string)
    const dateFields = [
      (user as any).createdAt,
      (user as any).created_at,
      (user as any).joinedAt,
      (user as any).joined_at,
      (user as any).createdDateTime,
      (user as any).joinDate,
      (user as any).join_date,
    ];
    
    for (const dateField of dateFields) {
      if (dateField !== undefined && dateField !== null) {
        // If it's a number (Unix timestamp), return it
        if (typeof dateField === 'number') {
          return dateField;
        }
        // If it's a string, try to parse it
        if (typeof dateField === 'string') {
          const parsed = Date.parse(dateField);
          if (!isNaN(parsed)) {
            return Math.floor(parsed / 1000); // Convert to Unix timestamp
          }
        }
      }
    }
    // Default to 0 if no date found (will sort to end)
    return 0;
  };

  // Convert ChannelUser[] to SoldTicket[] format for modal compatibility
  const convertChannelUsersToSoldTickets = useCallback((users: ChannelUser[]): SoldTicket[] => {
    return users.map((user) => {
      const email = (user as ChannelUser & { email?: string; userEmail?: string }).email ||
                    (user as ChannelUser & { email?: string; userEmail?: string }).userEmail ||
                    `${user.name || ''}${user.lastname || ''}@no-email.com`.toLowerCase() || 'no-email@no-email.com';
      
      return {
        ticketNo: '',
        ticketID: '',
        eventID: '',
        channelID: channelID || '',
        paymentID: '',
        name: user.name || '',
        lastname: user.lastname || '',
        email: email,
        phone: (user as ChannelUser & { phone?: string }).phone || '',
        isPaid: false,
        billing: {},
        createdAt: getJoinDate(user),
        usedAt: null,
        userAvatar: user.userAvatar || user.avatar || user.image || undefined,
        avatar: user.avatar || user.userAvatar || user.image || undefined,
        image: user.image || user.avatar || user.userAvatar || undefined,
        user: {
          userAvatar: user.userAvatar || user.avatar || user.image || undefined,
          avatar: user.avatar || user.userAvatar || user.image || undefined,
          image: user.image || user.avatar || user.userAvatar || undefined,
        },
        userID: user.userID || user.id || '',
        id: user.id || user.userID || '',
      } as SoldTicket;
    });
  }, [channelID]);

  // Helper function to check if user is admin or owner
  const checkIsAdminOrOwner = (user: ChannelUser): boolean => {
    const adminValue = (user as ChannelUser & { isAdmin?: boolean | "1" | 1 }).isAdmin;
    const ownerValue = (user as ChannelUser & { isOwner?: boolean | "1" | 1 }).isOwner;
    const isAdmin = adminValue === true || String(adminValue) === "1";
    const isOwner = ownerValue === true || String(ownerValue) === "1";
    return isAdmin || isOwner;
  };

  // Fetch channel users (admins and members)
  const fetchChannelUsers = useCallback(async () => {
    if (!channelID) {
      setAdmins([]);
      setMembers([]);
      return;
    }

    setUsersLoading(true);
    try {
      // Get token - use user token if available, otherwise get public token
      let effectiveToken = token;
      if (!effectiveToken) {
        effectiveToken = await getPublicToken();
      }

      if (!effectiveToken) {
        setAdmins([]);
        setMembers([]);
        setUsersLoading(false);
        return;
      }

      const response = await Services.SocialHiveGetChannelUsers(effectiveToken, channelID);
      if (response.data.status && response.data.result) {
        const users = response.data.result as ChannelUser[];
        
        // Filter admins (including owners)
        const adminUsers = users.filter((user) => checkIsAdminOrOwner(user));
        
        // Members list includes all users (organizers + regular members)
        // Sort by join date descending (newest first)
        const allUsers = [...users].sort((a, b) => {
          const dateA = getJoinDate(a);
          const dateB = getJoinDate(b);
          // If dates are equal or both 0, maintain original order
          if (dateA === dateB) return 0;
          // Sort descending (newest first)
          return dateB - dateA;
        });
        
        // If no dates were found (all dates are 0), reverse to show newest first
        // (assuming API returns oldest first when no date fields are available)
        const hasDates = allUsers.some(user => getJoinDate(user) > 0);
        if (!hasDates) {
          allUsers.reverse();
        }
        
        setAdmins(adminUsers);
        setMembers(allUsers);
      } else {
        setAdmins([]);
        setMembers([]);
      }
    } catch (error) {
      // Silently fail - if we can't fetch users, show empty state
      setAdmins([]);
      setMembers([]);
    } finally {
      setUsersLoading(false);
    }
  }, [channelID, token]);

  // Fetch users when channelID changes
  useEffect(() => {
    fetchChannelUsers();
  }, [fetchChannelUsers]);

  // Fetch announcements for the channel
  const fetchChannelAnnouncements = useCallback(async () => {
    if (!channelID) return;
    setAnnouncementsLoading(true);
    try {
      let effectiveToken = token;
      if (!effectiveToken) {
        effectiveToken = await getPublicToken();
      }
      if (!effectiveToken) {
        setAnnouncementsLoading(false);
        return;
      }
      const response = await Services.SocialHiveGetAnnouncements(effectiveToken, channelID);
      if (response.data.status && response.data.result) {
        const sorted = (response.data.result as Announcement[]).sort(
          (a, b) => Number(b.createdDateTime || 0) - Number(a.createdDateTime || 0)
        );
        setChannelAnnouncements(sorted);
      }
    } catch {
      // silently fail
    } finally {
      setAnnouncementsLoading(false);
    }
  }, [channelID, token]);

  useEffect(() => {
    fetchChannelAnnouncements();
  }, [fetchChannelAnnouncements]);

  // Handle deep link: ?announcement=<announcementID>
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const announcementID = params.get('announcement');
    if (!announcementID || !channelID) return;

    const openAnnouncementFromParam = async () => {
      // First try to find in already fetched list
      const found = channelAnnouncements.find(a => a.announcementID === announcementID);
      if (found) {
        setSelectedAnnouncementDetail(found);
        setShowAnnouncementDetailModal(true);
        return;
      }

      // Otherwise fetch the single announcement
      try {
        let effectiveToken = token;
        if (!effectiveToken) {
          effectiveToken = await getPublicToken();
        }
        if (!effectiveToken) return;
        const response = await Services.SocialHiveGetAnnouncement(effectiveToken, channelID, announcementID);
        if (response.data.status && response.data.result) {
          setSelectedAnnouncementDetail(response.data.result as Announcement);
          setShowAnnouncementDetailModal(true);
        }
      } catch {
        // silently fail
      }
    };

    openAnnouncementFromParam();
  }, [location.search, channelID, token, channelAnnouncements]);

  // Extract channelID from navigation state or find by slug
  useEffect(() => {
    if (!communityPath) {
      setSlugLoading(false);
      setHasCheckedForID(true);
      return;
    }

    // First, check if channelID is available in navigation state (from programmatic navigation)
    const stateChannelID = (location.state as { channelID?: string })?.channelID;
    if (stateChannelID) {
      setChannelID(stateChannelID);
      setSlugLoading(false);
      setHasCheckedForID(true);
      return;
    }

    // Extract channelID directly from URL
    const urlChannelID = extractChannelIdFromUrl(location.pathname);
    if (urlChannelID) {
      setChannelID(urlChannelID);
      setSlugLoading(false);
      setHasCheckedForID(true);
      return;
    }

    // If no ID found in URL, mark as checked and set loading to false (will show not found)
    setHasCheckedForID(true);
    setSlugLoading(false);
  }, [communityPath, location.state, location.pathname]);

  // Use null for token when not authenticated - hook will use public token
  const {
    community,
    upcomingEvents,
    pastEvents,
    participantCount,
    eventsCount,
    loading,
    error
  } = useCommunityDetails({
    token: token || null,
    channelID: channelID || '',
    loadingKey: 'community.details'
  });

  // Reset image errors when community changes
  useEffect(() => {
    setBannerError(false);
    setLogoError(false);
  }, [community]);

  // Combined loading state - show loading while extracting ID or fetching data
  // Also show loading when we have channelID but hook hasn't started loading yet (and no data/error)
  // OR when we have channelID but no community yet (initial load - prevent showing "not found" too early)
  const isLoading = slugLoading || (loading && !!channelID) || (!!channelID && !loading && !community && !error) || (!!channelID && !community && !error && !hasCheckedForID);

  // Get user's joined channels to check if they're joined (only when authenticated)
  const { channels: joinedChannels } = useChannels({
    token: token || null,
    userID: userId,
    popular: false
  });

  // Check if user is joined to this channel and if they're admin
  const { isJoined, isAdmin } = useMemo(() => {
    if (!channelID) return { isJoined: false, isAdmin: false };
    const userChannel = joinedChannels.find(
      (ch) => String(ch.channelID) === String(channelID)
    );
    return {
      isJoined: !!userChannel,
      isAdmin: userChannel ? String(userChannel.isAdmin) === "1" : false
    };
  }, [joinedChannels, channelID]);

  // Check if channel is private and user is not a member
  const isPrivateChannelAndNotMember = useMemo(() => {
    // If we have community data, check directly
    if (community) {
      const isPrivate = community.isPublic === "0";
      return isPrivate && !isJoined;
    }
    
    // If we have channelID but no community data and user is not a member,
    // it's likely a private channel access issue
    if (channelID && !isJoined && error) {
      // Assume it's a private channel if we have channelID but can't access it
      return true;
    }
    
    return false;
  }, [community, isJoined, channelID, error]);

  // Confirmation dialog state
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const handleEventClick = (event: EventListItem) => {
    navigate(generateEventUrl(event.eventTitle || event.title, event.eventID));
  };

  const handleJoinClick = () => {
    if (!token || !userId) {
      toastError(getTranslation('home.loginRequired') || 'Please login to join communities');
      // Redirect to login with current URL as redirect parameter
      const currentPath = location.pathname;
      const redirectUrl = encodeURIComponent(currentPath);
      navigate(`/login?redirect=${redirectUrl}`);
      return;
    }
    setShowConfirmDialog(true);
  };

  const handleAdminPanelClick = () => {
    if (!community) return;
    const channelTitle = community.channelTitle || community.title;
    if (!channelTitle) return;
    const slug = createSlug(channelTitle);
    navigate(`/communities/${slug}/admin`);
  };

  const handleConfirm = async () => {
    setShowConfirmDialog(false);
    if (!token || !userId || !channelID) {
      toastError(getTranslation('communities.joinError'));
      return;
    }

    try {
      if (isJoined) {
        const response = await Services.SocialHiveLeaveChannel(token, userId, channelID);
        if (response.data.status) {
          toastSuccess(getTranslation('communities.leaveSuccess'));
          // Refresh page or reload community data
          window.location.reload();
        } else {
          toastError(response.data.message || getTranslation('communities.leaveError'));
        }
      } else {
        const response = await Services.SocialHiveJoinToChannel(token, userId, channelID);
        if (response.data.status) {
          // Check if it was a join request (for private channels) or direct join
          const responseMessage = response.data.message || '';
          const isJoinRequest = responseMessage.toLowerCase().includes('request') || 
            responseMessage.toLowerCase().includes('pending') ||
            responseMessage.toLowerCase().includes('approval');
          
          if (isJoinRequest) {
            toastSuccess(getTranslation('communities.joinRequestSent') || getTranslation('communities.joinRequestSuccess'));
          } else {
            toastSuccess(getTranslation('communities.joinSuccess'));
          }
          
          // Refresh page or reload community data
          window.location.reload();
        } else {
          toastError(response.data.message || getTranslation('communities.joinError'));
        }
      }
    } catch (error: any) {
      toastError(error.response?.data?.message || getTranslation('communities.joinError'));
    }
  };

  const handleCancel = () => {
    setShowConfirmDialog(false);
  };

  if (isLoading) {
    return (
      <>
        <SEO title="Loading Community" noindex />
        <div className={styles.communityPage}>
          <LoadingSkeleton width="100%" height={400} />
        </div>
      </>
    );
  }

  // Show private channel error if channel is private and user is not a member
  // Check this BEFORE the "not found" check
  if (isPrivateChannelAndNotMember && channelID) {
    const channelName = community?.channelTitle || community?.title;
    return (
      <>
        <SEO title={channelName || getTranslation('communities.communityNotFound') || 'Community'} noindex />
        <div className={styles.communityPage}>
          <PrivateChannelError
            channelID={channelID}
            channelName={channelName}
          />
        </div>
      </>
    );
  }

  // Only show "not found" if we've finished extracting the ID and confirmed there isn't one
  // Must have checked for ID and not be loading, and have no channelID or error
  if (hasCheckedForID && !isLoading && (!channelID || (error && !isPrivateChannelAndNotMember) || (!community && !isPrivateChannelAndNotMember && !loading))) {
    return (
      <>
        <SEO
          title={getTranslation('communities.communityNotFound') || 'Community not found'}
          description={error || 'The community you are looking for does not exist.'}
          noindex
        />
        <div className={styles.communityPage}>
          <EmptyState 
            title={getTranslation('communities.communityNotFound') || 'Community not found'} 
            description={error || 'The community you are looking for does not exist.'} 
          />
        </div>
      </>
    );
  }

  // At this point, community should exist, but add a safety check
  if (!community) {
    return (
      <>
        <SEO
          title={getTranslation('communities.communityNotFound') || 'Community not found'}
          description="The community data could not be loaded."
          noindex
        />
        <div className={styles.communityPage}>
          <EmptyState 
            title={getTranslation('communities.communityNotFound') || 'Community not found'} 
            description="The community data could not be loaded." 
          />
        </div>
      </>
    );
  }

  const bannerUrl = community.topBanner || null;
  const hasBanner = !!bannerUrl && !bannerError;
  const logoUrl = community.logo || community.channelLogo || null;
  const hasLogo = !!logoUrl && !logoError;
  const channelName = displayValue(community.channelTitle || community.title);
  const tabEvents = activeTab === 'upcoming' ? upcomingEvents : pastEvents;
  const displayEvents = isAdmin ? tabEvents : tabEvents.filter((e) => e.isPublic !== "3");

  const canonicalUrl =
    typeof window !== 'undefined' ? `${window.location.origin}${location.pathname}` : undefined;

  const seoTitle = (community.channelTitle || community.title) ? channelName : 'Community';
  const seoDescription =
    (community.description || '')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 160) + ((community.description || '').trim().length > 160 ? '…' : '');

  const structuredData = canonicalUrl
    ? generateOrganizationSchema({
        community,
        url: canonicalUrl,
        logoUrl: hasLogo ? (logoUrl as string) : undefined,
      })
    : undefined;

  // Helper functions for user display
  const getUserAvatar = (user: ChannelUser): string | null => {
    return user.userAvatar || user.avatar || user.image || null;
  };

  const getUserName = (user: ChannelUser): string => {
    const name = user.name || '';
    const lastname = user.lastname || '';
    const fullName = `${name} ${lastname}`.trim();
    return fullName || getTranslation('common.unknownUser');
  };

  return (
    <>
      <SEO
        title={seoTitle}
        description={seoDescription}
        image={hasBanner ? (bannerUrl as string) : undefined}
        url={canonicalUrl}
        type="website"
        structuredData={structuredData}
        lang={language.language}
      />
      <div className={styles.communityPage}>
        <div className={styles.mainContent}>
        {/* Community Info Card */}
        <div className={styles.communityInfoCard}>
          {/* Community Banner with Logo and Title Overlaid */}
          <div className={styles.bannerContainer}>
            {hasBanner ? (
              <>
                {bannerLoading && <div className="image-shimmer" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, borderRadius: 'inherit' }} />}
                <img
                  src={bannerUrl}
                  alt={channelName}
                  className={styles.bannerImage}
                  onLoad={() => setBannerLoading(false)}
                  onError={() => {
                    setBannerLoading(false);
                    setBannerError(true);
                  }}
                />
              </>
            ) : (
              <GrayBanner className={styles.bannerImage} id={community?.channelID} />
            )}
            {/* Community Logo - Half on Banner, Half on White Container */}
            <div className={styles.logoContainer}>
              {hasLogo ? (
                <>
                  {logoLoading && <div className="image-shimmer" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, borderRadius: '12px' }} />}
                  <img
                    src={logoUrl}
                    alt={channelName}
                    className={styles.logoImage}
                    onLoad={() => setLogoLoading(false)}
                    onError={() => {
                      setLogoLoading(false);
                      setLogoError(true);
                    }}
                  />
                </>
              ) : (
                <ChannelInitialIcon 
                  channelName={channelName} 
                  className={styles.logoImage}
                  size={120}
                  borderRadius="12px"
                />
              )}
            </div>
          </div>

          {/* Community Details */}
          <div className={styles.communityDetails}>
            {/* Community Title - Under Avatar */}
            <h2 className={styles.communityTitle}>
              {channelName}
            </h2>
            {/* Community Description */}
            {community?.description && (
              <p className={styles.communityDescription}>
                {community.description}
              </p>
            )}
            <div className={styles.communityDetailsRow}>
              <span className={styles.communityDetailItem}>
                <span className={styles.communityDetailBold}>
                  {participantCount !== null && participantCount !== undefined 
                    ? participantCount.toLocaleString()
                    : getTranslation('common.notAvailable')}
                </span>
                <span className={styles.communityDetailRegular}>
                  {' '}{getTranslation('communities.participantSingular')}
                </span>
              </span>
              <span className={styles.communityDetailItem}>
                <span className={styles.communityDetailBold}>
                  {eventsCount !== null && eventsCount !== undefined 
                    ? eventsCount
                    : getTranslation('common.notAvailable')}
                </span>
                <span className={styles.communityDetailRegular}>
                  {' '}{getTranslation('communities.eventsSingular')}
                </span>
              </span>
              <span className={styles.communityDetailItem}>
                {community?.isPublic === "1" ? (
                  <>
                    <span className={styles.communityDetailBold}>
                      {getTranslation('communities.publicCommunityBold')}
                    </span>
                    <span className={styles.communityDetailRegular}>
                      {' '}{getTranslation('communities.publicCommunityRegular')}
                    </span>
                  </>
                ) : (
                  <>
                    <span className={styles.communityDetailBold}>
                      {getTranslation('communities.privateCommunityBold')}
                    </span>
                    <span className={styles.communityDetailRegular}>
                      {' '}{getTranslation('communities.privateCommunityRegular')}
                    </span>
                  </>
                )}
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className={styles.buttonContainer}>
            {isAdmin ? (
              <>
                <Button
                  variant="orange"
                  size="medium"
                  onClick={handleAdminPanelClick}
                  className={styles.actionButton}
                >
                  {getTranslation('communities.manageCommunity') || 'Manage Community'}
                </Button>
              </>
            ) : (
              <Button
              variant={isJoined ? 'success' : 'primary'}
              size="medium"
              onClick={handleJoinClick}
              className={styles.actionButton}
            >
              {isJoined ? getTranslation('communities.joined') : getTranslation('communities.joinCommunity')}
            </Button>
            )}
            <Button
              variant="secondary"
              size="medium"
              onClick={async () => {
                if (!channelID) return;
                const shareUrl = `${window.location.origin}${generateCommunityShareUrl(String(channelID))}`;

                if (navigator.share) {
                  try {
                    await navigator.share({
                      title: community.channelTitle || community.title,
                      text: community.description?.substring(0, 200),
                      url: shareUrl,
                    });
                  } catch (error) {
                    // User cancelled or error occurred, do nothing
                  }
                } else {
                  try {
                    await navigator.clipboard.writeText(shareUrl);
                    toastSuccess(getTranslation('common.linkCopied'));
                  } catch (error) {
                    // Clipboard API failed, could show error toast here if needed
                  }
                }
              }}
              className={styles.actionButton}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
                {/* @ts-ignore - react-icons compatibility with React 19 */}
                <HiShare />
                <span>{getTranslation('common.share') || 'Share'}</span>
              </span>
            </Button>
          </div>
        </div>

        {/* Events Section */}
        <div className={styles.eventsSection}>
          <div className={styles.eventsListContainer}>
            {/* Event Tabs */}
            <div className={styles.tabsHeader}>
              <button
                className={`${styles.tabButton} ${activeTab === 'upcoming' ? styles.tabButtonActive : styles.tabButtonInactive}`}
                onClick={() => setActiveTab('upcoming')}
                type="button"
              >
                {getTranslation('communities.upcomingEvents') || 'Yaklaşan Etkinlikler'}
              </button>
              <button
                className={`${styles.tabButton} ${activeTab === 'past' ? styles.tabButtonActive : styles.tabButtonInactive}`}
                onClick={() => setActiveTab('past')}
                type="button"
              >
                {getTranslation('communities.pastEvents') || 'Geçmiş Etkinlikler'}
              </button>
            </div>
            
            {/* Separator */}
            <div className={styles.tabsSeparator}></div>

            {/* Events List */}
            <div className={styles.eventsList}>
              {displayEvents.length > 0 ? (
                <>
                  {displayEvents.map((event, index) => (
                    <CommunityEventCard
                      key={event.eventID}
                      event={event}
                      onClick={handleEventClick}
                      isLast={index === displayEvents.length - 1}
                      showParticipantCount={true}
                    />
                  ))}
                </>
              ) : (
                <EmptyState 
                  title={
                    activeTab === 'upcoming'
                      ? getTranslation('communities.noUpcomingEvents') || 'No upcoming events'
                      : getTranslation('communities.noPastEvents') || 'No past events'
                  }
                />
              )}
            </div>
          </div>
        </div>

        {/* Announcements Section */}
        {channelAnnouncements.length > 0 && (
          <div className={styles.eventsSection}>
            <div className={styles.eventsListContainer}>
              <div className={styles.tabsHeader}>
                <span className={`${styles.tabButton} ${styles.tabButtonActive}`}>
                  {getTranslation('communities.announcements')}
                </span>
              </div>
              <div className={styles.tabsSeparator}></div>
              <div className={styles.eventsList}>
                {channelAnnouncements.map((announcement) => {
                  const creator = Array.isArray(announcement.createdBy)
                    ? announcement.createdBy[0]
                    : announcement.createdBy;
                  const creatorName = creator
                    ? `${creator.name} ${creator.lastname}`.trim()
                    : '';

                  return (
                    <button
                      key={announcement.announcementID}
                      type="button"
                      className={styles.announcementCard}
                      onClick={() => {
                        setSelectedAnnouncementDetail(announcement);
                        setShowAnnouncementDetailModal(true);
                      }}
                    >
                      <img
                        src={announcement.image || logoUrl || ''}
                        alt={announcement.title}
                        className={styles.announcementCardImage}
                        style={!announcement.image && logoUrl ? { objectPosition: 'center center' } : undefined}
                      />
                      <div className={styles.announcementCardContent}>
                        <h4 className={styles.announcementCardTitle}>{announcement.title}</h4>
                        <p className={styles.announcementCardDescription}>{announcement.description}</p>
                        <div className={styles.announcementCardMeta}>
                          {creator?.userAvatar && (
                            <img
                              src={creator.userAvatar}
                              alt={creatorName}
                              className={styles.announcementCardAvatar}
                            />
                          )}
                          <span className={styles.announcementCardCreator}>{creatorName}</span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Sidebar */}
      <div className={styles.sidebar}>
        {/* Admins Section */}
        <div className={`${styles.sidebarCard} ${styles.organizersCard}`}>
          <div className={styles.organizersHeader}>
            <h3 className={styles.sidebarCardTitle}>
              {getTranslation('communities.organizers') || 'Organizatörler'}
            </h3>
            <button
              className={styles.detailsButton}
              onClick={() => setShowOrganizersModal(true)}
              type="button"
            >
              {getTranslation('common.details') || 'Details'}
            </button>
          </div>
          {usersLoading ? (
            <div className={styles.adminsList}>
              {[1, 2, 3, 4].map((index) => (
                <div key={index} className={styles.adminItem}>
                  <LoadingSkeleton variant="circular" width={40} height={40} />
                </div>
              ))}
            </div>
          ) : admins.length > 0 ? (
            <div className={styles.adminsList}>
              {admins.map((admin, index) => {
                const avatarUrl = getUserAvatar(admin);
                const adminName = getUserName(admin);
                
                return (
                  <UserProfileLink
                    key={admin.userID || admin.id || index}
                    userId={admin.userID || admin.id}
                    name={admin.name}
                    lastname={admin.lastname}
                  >
                  <div className={styles.adminItem}>
                    <div className={styles.adminAvatarWrapper}>
                      {avatarUrl ? (
                        <img
                          src={avatarUrl}
                          alt={adminName}
                          className={styles.adminAvatar}
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const parent = target.parentElement;
                            if (parent) {
                              const fallback = parent.querySelector(`.${styles.adminAvatarFallback}`);
                              if (fallback) {
                                (fallback as HTMLElement).style.display = 'flex';
                              }
                            }
                          }}
                        />
                      ) : null}
                      <div
                        className={styles.adminAvatarFallback}
                        style={{
                          display: avatarUrl ? 'none' : 'flex',
                          backgroundColor: getColorForName(adminName),
                          color: '#ffffff'
                        }}
                      >
                        <ChannelInitialIcon
                          channelName={adminName}
                          size={64}
                        />
                      </div>
                      <div className={styles.crownIcon}>
                        {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
                        {/* @ts-ignore - react-icons compatibility with React 19 */}
                        <HiStar />
                      </div>
                    </div>
                    <p className={styles.adminName}>{adminName}</p>
                  </div>
                  </UserProfileLink>
                );
              })}
            </div>
          ) : (
            <p className={styles.emptyText}>{getTranslation('communities.noOrganizers')}</p>
          )}
        </div>

        {/* Participants Section */}
        <div className={`${styles.sidebarCard} ${styles.membersCard}`}>
          <div className={styles.participantsHeader}>
            <h3 className={styles.sidebarCardTitle}>
              {getTranslation('communities.members') || 'Üyeler'}
            </h3>
            {members.length > 0 && (
              <span className={styles.memberCount}>{members.length.toLocaleString()}</span>
            )}
            {members.length > 0 && (
              <button
                className={styles.viewAllButton}
                onClick={() => setShowParticipantModal(true)}
              >
                {getTranslation('communities.viewAll') || 'Hepsini gör'}
              </button>
            )}
          </div>
          {usersLoading ? (
            <div className={styles.membersGrid}>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((index) => (
                <div key={index} className={styles.memberAvatarWrapperSkeleton}>
                  <LoadingSkeleton variant="circular" width="100%" height="100%" />
                </div>
              ))}
            </div>
          ) : members.length > 0 ? (
            <div className={styles.membersGrid}>
              {members.slice(0, 12).map((member, index) => {
                const avatarUrl = getUserAvatar(member);
                const memberName = getUserName(member);
                
                return (
                  <UserProfileLink
                    key={member.userID || member.id || index}
                    userId={member.userID || member.id}
                    name={member.name}
                    lastname={member.lastname}
                  >
                  <div
                    className={styles.memberAvatarWrapper}
                  >
                    {avatarUrl ? (
                      <img
                        src={avatarUrl}
                        alt={memberName}
                        className={styles.memberAvatar}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const parent = target.parentElement;
                          if (parent) {
                            const fallback = parent.querySelector(`.${styles.memberAvatarFallback}`);
                            if (fallback) {
                              (fallback as HTMLElement).style.display = 'flex';
                            }
                          }
                        }}
                      />
                    ) : null}
                    <div
                      className={styles.memberAvatarFallback}
                      style={{
                        display: avatarUrl ? 'none' : 'flex',
                        backgroundColor: getColorForName(memberName),
                        color: '#ffffff'
                      }}
                    >
                      <ChannelInitialIcon
                        channelName={memberName}
                        size={48}
                      />
                    </div>
                  </div>
                  </UserProfileLink>
                );
              })}
            </div>
          ) : (
            <p className={styles.emptyText}>{getTranslation('communities.noMembers')}</p>
          )}
        </div>
      </div>

      {/* Participant List Modal */}
      <ParticipantListModal
        isOpen={showParticipantModal}
        onClose={() => setShowParticipantModal(false)}
        participants={convertChannelUsersToSoldTickets(members)}
        tickets={[]}
        getTranslation={getTranslation}
      />

      {/* Organizers Modal */}
      <OrganizersModal
        isOpen={showOrganizersModal}
        onClose={() => setShowOrganizersModal(false)}
        organizers={admins}
        getTranslation={getTranslation}
      />

      {/* Announcement Detail Modal */}
      <Modal
        isOpen={showAnnouncementDetailModal}
        onClose={() => {
          setShowAnnouncementDetailModal(false);
          setSelectedAnnouncementDetail(null);
          // Clean up query param
          const params = new URLSearchParams(location.search);
          if (params.has('announcement')) {
            params.delete('announcement');
            const newUrl = params.toString()
              ? `${location.pathname}?${params.toString()}`
              : location.pathname;
            navigate(newUrl, { replace: true });
          }
        }}
        title={selectedAnnouncementDetail?.title || getTranslation('communities.announcementDetails')}
        variant="default"
      >
        {selectedAnnouncementDetail && (() => {
          const creator = Array.isArray(selectedAnnouncementDetail.createdBy)
            ? selectedAnnouncementDetail.createdBy[0]
            : selectedAnnouncementDetail.createdBy;
          const creatorName = creator
            ? `${creator.name} ${creator.lastname}`.trim()
            : '';
          const dateStr = selectedAnnouncementDetail.createdDateTime
            ? new Date(Number(selectedAnnouncementDetail.createdDateTime) * 1000).toLocaleDateString(undefined, {
                year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
              })
            : '';

          return (
            <div className={styles.announcementDetailContent}>
              <img
                src={selectedAnnouncementDetail.image || logoUrl || ''}
                alt={selectedAnnouncementDetail.title}
                className={styles.announcementDetailImage}
                style={!selectedAnnouncementDetail.image && logoUrl ? { objectPosition: 'center center' } : undefined}
              />
              <div className={styles.announcementDetailMeta}>
                <span className={styles.announcementCardType}>{selectedAnnouncementDetail.type}</span>
              </div>
              {selectedAnnouncementDetail.subTitle && (
                <p className={styles.announcementDetailSubtitle}>{selectedAnnouncementDetail.subTitle}</p>
              )}
              <p className={styles.announcementDetailDescription}>{selectedAnnouncementDetail.description}</p>
              <div className={styles.announcementDetailFooter}>
                {creator?.userAvatar && (
                  <img
                    src={creator.userAvatar}
                    alt={creatorName}
                    className={styles.announcementCardAvatar}
                  />
                )}
                <span className={styles.announcementCardCreator}>{creatorName}</span>
                <span className={styles.announcementCardDate}>{dateStr}</span>
              </div>
            </div>
          );
        })()}
      </Modal>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className={styles.modalOverlay} onClick={handleCancel}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 className={styles.modalTitle}>
              {isJoined ? getTranslation('communities.confirmLeave') : getTranslation('communities.confirmJoin')}
            </h3>
            <div className={styles.modalActions}>
              <Button
                variant="secondary"
                size="large"
                onClick={handleCancel}
                className={styles.modalButton}
              >
                {getTranslation('common.cancel')}
              </Button>
              <Button
                variant="primary"
                size="large"
                onClick={handleConfirm}
                className={styles.modalButton}
              >
                {isJoined ? getTranslation('communities.leaveCommunity') : getTranslation('communities.joinCommunity')}
              </Button>
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default CommunityPage;

